import * as React from 'react';
import { computed, makeObservable, reaction } from 'mobx';
import { Component } from 'tsdi';
import { PresentationStep } from './presentation';
import { injectTSDI } from '@core/tsdi';
import { ChallengePresentationStore } from './presentation/store';
import { bind } from 'lodash-decorators';
import { ChallengeRulesStore } from './rules/store';
import { RulesStep } from './rules';
import {
    ChallengePersonalRuleSetDto,
    ChallengeStatus,
    ChallengeTeamRuleSetDto,
    CommunityChallengeCreateRequestDto
} from '@core/api/ChallengeClient';
import { buildChallengeRulePayload } from './rules/rule-payload';
import { autodisposer } from '@core/reactions';
import { ChallengeDataStore } from './data-store';
import { assertDefined } from '@components/utils/error';
import { generateDateRange } from '@components/utils/time';
import { toZonedDateTime } from '@ms-rewards/date-lib';
import { TeamRulesStore } from '@src/communities/community/challenges/challenge-form/rules/team-rules/store';
import { PersonalRulesStore } from '@src/communities/community/challenges/challenge-form/rules/personal-rules/store';
import { GroupRulesStore } from '@src/communities/community/challenges/challenge-form/rules/group-rules/store';
import { ChallengeConsentsStore } from '@src/communities/community/challenges/challenge-form/consents/store';
import { ConsentsStep } from '@src/communities/community/challenges/challenge-form/consents';
import { ChallengeRewardStore } from './reward/store';
import { RewardStep } from './reward';

export enum StepName {
    PRESENTATION = 'PRESENTATION',
    RULES = 'RULES',
    REWARD = 'REWARD',
    CONSENTS = 'CONSENTS'
}

export enum StepStatus {
    CREATING = 'CREATING',
    COMPLETED = 'COMPLETED',
    DISABLED = 'DISABLED',
    INCOMPLETE = 'INCOMPLETE',
    MODIFIED = 'MODIFIED',
    UPDATING = 'UPDATING',
    ERROR = 'ERROR'
}

interface StepProps {
    name: string;
    key: StepName;
    error?: string;
    status: StepStatus;
    isDirty: boolean;
    isActive: boolean;
    Content: React.ComponentType;
}

@Component
export class ChallengeStore {
    constructor() {
        makeObservable(this, {
            steps: computed,
            isDirty: computed
        });
    }

    private presentationStore = injectTSDI(ChallengePresentationStore);
    private rulesStore = injectTSDI(ChallengeRulesStore);
    private teamRulesStore = injectTSDI(TeamRulesStore);
    private personalRulesStore = injectTSDI(PersonalRulesStore);
    private groupRulesStore = injectTSDI(GroupRulesStore);
    private challengeDataStore = injectTSDI(ChallengeDataStore);
    private consentsStore = injectTSDI(ChallengeConsentsStore);
    private rewardStore = injectTSDI(ChallengeRewardStore);

    public get steps(): Record<StepName, StepProps> {
        return {
            PRESENTATION: {
                name: 'Presentation',
                key: StepName.PRESENTATION,
                status: this.presentationStore.state,
                isDirty: this.presentationStore.formIsDirty,
                isActive: this.presentationStore.isActive,
                Content: PresentationStep
            },
            RULES: {
                key: StepName.RULES,
                name: 'Rules',
                status: this.rulesStore.state,
                isDirty: this.rulesStore.formIsDirty,
                isActive: this.rulesStore.isActive,
                Content: RulesStep
            },
            REWARD: {
                key: StepName.REWARD,
                name: 'Reward',
                status: this.rewardStore.state,
                isDirty: this.rewardStore.formIsDirty,
                isActive: this.rewardStore.isActive,
                Content: RewardStep
            },
            CONSENTS: {
                key: StepName.CONSENTS,
                name: 'Consents',
                status: this.consentsStore.state,
                isDirty: this.consentsStore.formIsDirty,
                isActive: this.consentsStore.isActive,
                Content: ConsentsStep
            }
        };
    }

    public get stepsMap() {
        return Object.values(this.steps);
    }

    public get isUpdating() {
        return this.stepsMap.some(
            (step) => step.status === StepStatus.UPDATING
        );
    }

    public get isDirty() {
        return this.stepsMap.some((step) => step.isDirty);
    }

    public get hasErrors() {
        return this.stepsMap.some((step) => step.status === StepStatus.ERROR);
    }

    public get isModified() {
        return this.stepsMap.some(
            (step) => step.status === StepStatus.MODIFIED
        );
    }

    public get createMode(): boolean {
        return this.challengeDataStore.challengeConfiguration === undefined;
    }

    public static challengeStatusContains(
        permission: ChallengeStatus[],
        status?: ChallengeStatus
    ): boolean {
        if (status && permission.some((option) => option === status)) {
            return false;
        }

        return true;
    }

    @bind
    public async validateAllForms() {
        await this.presentationStore.validatePresentation();

        const presentationValidation =
            await this.presentationStore.form.validate();
        const rulesValidation = await this.rulesStore.validateRules();
        const consentsValidation = await this.consentsStore.validateConsents();
        const rewardValidation = await this.rewardStore.validateReward();

        return (
            presentationValidation.hasError ||
            rulesValidation ||
            consentsValidation ||
            rewardValidation
        );
    }

    @bind
    public async createChallenge(communityId: number) {
        await this.validateAllForms();

        this.stepsMap.forEach((step) => {
            step.status = StepStatus.UPDATING;
        });

        // Snapshot new badge data BEFORE createChallengeRequest
        // because the MobX reaction in initialize() resets the form
        const badgeSnapshot = this.rewardStore.snapshotNewBadgeData();

        const requestData = this.generateRequestData(communityId);
        const challenge =
            await this.challengeDataStore.createChallengeRequest(requestData);

        // If NEW_BADGE was selected, create the badge and link it.
        // This is a separate step after challenge creation — if it fails,
        // the challenge exists but has no reward. The user can retry via the edit form.
        if (badgeSnapshot && challenge?.id) {
            try {
                const rewardConfig =
                    await this.rewardStore.createChallengeBadgeFromSnapshot(
                        badgeSnapshot,
                        challenge.id,
                        communityId
                    );
                await this.rewardStore.rewardUpdateRequest.request({
                    challengeId: challenge.id,
                    data: rewardConfig
                });
            } catch (error) {
                // Re-throw with context so the UI can show a meaningful message
                throw new Error(
                    `Challenge created but badge linking failed. ` +
                        `Edit the challenge to retry. ` +
                        `Original error: ${error instanceof Error ? error.message : error}`
                );
            }
        }
    }

    private generateTeamRuleSet(): ChallengeTeamRuleSetDto | undefined {
        if (this.rulesStore.form.$.teamRules.value) {
            return this.teamRulesStore.generateRequestData(
                this.teamRulesStore.form
            );
        }
        return undefined;
    }

    private generatePersonalRuleSet(): ChallengePersonalRuleSetDto {
        return this.personalRulesStore.generateRequestData(
            this.personalRulesStore.form
        );
    }

    private generateGroupRules() {
        if (this.rulesStore.form.$.groupRules.value) {
            const groupRulesData = this.groupRulesStore.generateRequestData(
                this.groupRulesStore.form
            );

            return groupRulesData;
        }

        return [];
    }

    private generateConsents() {
        if (this.consentsStore.form.$.consents.value) {
            return this.consentsStore.generateRequestData(
                this.consentsStore.form
            );
        } else {
            return [];
        }
    }

    public generateRequestData(
        communityId: number
    ): CommunityChallengeCreateRequestDto {
        assertDefined(this.presentationStore.form.$.photo.value);

        assertDefined(this.rulesStore.form.$.timeFrom.value);
        assertDefined(this.rulesStore.form.$.timeTo.value);
        assertDefined(this.rulesStore.form.$.timeZone.value);

        const dateTimeRange = generateDateRange(
            this.rulesStore.form.$.timeFrom.value,
            this.rulesStore.form.$.timeTo.value,
            this.rulesStore.form.$.timeZone.value
        );

        const teamRuleSet = this.generateTeamRuleSet();
        const personalRuleSet = this.generatePersonalRuleSet();

        const groupRules = this.generateGroupRules();
        const consents = this.generateConsents();
        return {
            communityId,
            name: this.presentationStore.form.$.name.value,
            prizeDescription:
                this.presentationStore.form.$.prizeDescription.value,
            photo: this.presentationStore.form.$.photo.value,
            dateTimeRange,
            autoJoinEnabled: this.rulesStore.form.$.autoJoinEnabled.value,
            recordAttempts: false,
            joinDeadline: this.rulesStore.form.$.joinDeadline.value
                ? toZonedDateTime(
                      this.rulesStore.form.$.joinDeadline.value,
                      this.rulesStore.form.$.timeZone.value
                  ).toJSON()
                : undefined,

            ...buildChallengeRulePayload(this.rulesStore.form),
            personalRuleSet,

            ...(teamRuleSet && { teamRuleSet }),
            groupRules,
            joinConsents: consents,
            rewardConfig: this.rewardStore.generateRequestData()
        };
    }

    @bind
    public onStepToggle(step: StepName) {
        if (step === StepName.PRESENTATION) {
            this.presentationStore.onCollapsedToggle();
        } else if (step === StepName.RULES) {
            this.rulesStore.onCollapsedToggle();
        } else if (step === StepName.REWARD) {
            this.rewardStore.onCollapsedToggle();
        } else if (step === StepName.CONSENTS) {
            this.consentsStore.onCollapsedToggle();
        }
    }

    @bind
    public async resetForms() {
        this.presentationStore.formResetKey++;

        this.presentationStore.form.reset();
        this.rulesStore.form.reset();
        this.teamRulesStore.form.reset();
        this.personalRulesStore.form.reset();
        this.groupRulesStore.form.reset();
        this.consentsStore.form.reset();
        this.rewardStore.form.reset();
        this.rewardStore.createdBadgeTypeId = undefined;
    }

    @bind
    public async updateChallenge() {
        await this.validateAllForms();

        this.stepsMap.forEach((step) => {
            step.status = StepStatus.UPDATING;
        });
        if (this.presentationStore.state === StepStatus.MODIFIED) {
            await this.presentationStore.updateChallengePresentation();
        }
        if (this.rulesStore.state === StepStatus.MODIFIED) {
            await this.rulesStore.updateChallengeRules();
        }

        if (this.consentsStore.state === StepStatus.MODIFIED) {
            await this.consentsStore.updateConsents();
        }

        if (this.rewardStore.state === StepStatus.MODIFIED) {
            await this.rewardStore.updateReward();
        }

        this.stepsMap.forEach((step) => {
            step.status = StepStatus.COMPLETED;
        });
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    if (configuration) {
                        // TODO
                        // this.setStepStatus(configuration);
                    }
                }
            )
        ];
    }
}
